import { useNavigate } from "@tanstack/react-router";
import type { AssignableRole } from "@unbox/api";
import { Plus, SearchMd } from "@untitledui/icons";
import { useState } from "react";
import { useAdminUsers } from "@/api/admin/users";
import { RoleSelect } from "@/components/admin/role-select";
import { Avatar } from "@/components/base/avatar/avatar";
import { Button } from "@/components/base/buttons/button";
import { Checkbox } from "@/components/base/checkbox/checkbox";
import { Input } from "@/components/base/input/input";
import { getUserInitials } from "@/utils/user-initials";

type Props = {
  // Selected users → the role to grant them for this workspace/merchant (UNB-2001).
  value: Record<string, AssignableRole>;
  onChange: (next: Record<string, AssignableRole>) => void;
  // The roles the caller allows (e.g. create-workspace = 3, create-merchant = 2).
  roleOptions: AssignableRole[];
  onNavigateAway?: () => void;
};

export const WorkspaceUsersPicker = ({ value, onChange, roleOptions, onNavigateAway }: Props) => {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");

  const { data: usersData } = useAdminUsers(
    { offset: 0, limit: 500 },
    { column: "firstName", direction: "asc" },
    { text: "" },
  );

  const defaultRole: AssignableRole = roleOptions.includes("user") ? "user" : roleOptions[0];

  const toggleUser = (id: string) => {
    const next = { ...value };
    if (id in next) delete next[id];
    else next[id] = defaultRole;
    onChange(next);
  };

  const setRole = (id: string, role: AssignableRole) => {
    // Picking a role also selects the user.
    onChange({ ...value, [id]: role });
  };

  const filteredUsers = (usersData?.users ?? []).filter((u) => {
    const fullName = `${u.firstName ?? ""} ${u.lastName ?? ""}`.trim();
    const term = search.toLowerCase();
    return fullName.toLowerCase().includes(term) || u.email.toLowerCase().includes(term);
  });

  return (
    <div className="flex flex-col flex-1 overflow-hidden gap-3">
      <div className="shrink-0 flex flex-col gap-1">
        <h2 className="text-xl font-semibold text-primary">Invite users to this workspace</h2>
        <p className="text-sm text-tertiary">Everyone you add will have access to all merchants by default.</p>
        <p className="text-sm text-tertiary">You can restrict visibility per merchant after creation.</p>
      </div>

      <div className="shrink-0">
        <Input
          placeholder="Search"
          value={search}
          onChange={setSearch}
          icon={SearchMd}
          wrapperClassName="bg-primary ring-primary rounded-xl"
          iconClassName="text-fg-quaternary"
          inputClassName="text-primary placeholder:text-placeholder"
        />
      </div>

      <div className="flex-1 overflow-y-auto min-h-0 flex flex-col gap-2">
        {filteredUsers.length === 0 && <p className="text-center text-sm text-tertiary py-6">No users found</p>}

        {filteredUsers.map((user) => {
          const isSelected = user.id in value;
          const displayName = `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim() || user.email;
          return (
            <div
              key={user.id}
              className={`w-full shrink-0 flex items-center gap-3 px-4 py-3 overflow-hidden rounded-xl border transition-colors ${
                isSelected ? "border-brand-600 bg-white/5" : "border-primary"
              }`}
            >
              <button
                type="button"
                onClick={() => toggleUser(user.id)}
                className="flex items-center gap-3 flex-1 min-w-0 text-left"
              >
                <Avatar size="sm" initials={getUserInitials(user.firstName, user.lastName)} alt={displayName} />
                <div className="flex flex-col flex-1 min-w-0">
                  <span className="text-sm font-medium text-primary truncate">{displayName}</span>
                  <span className="text-xs text-tertiary truncate">{user.email}</span>
                </div>
              </button>
              <RoleSelect
                aria-label={`Role for ${displayName}`}
                options={roleOptions}
                value={value[user.id] ?? defaultRole}
                onChange={(role) => setRole(user.id, role)}
              />
              <Checkbox
                size="md"
                isSelected={isSelected}
                onChange={() => toggleUser(user.id)}
                aria-label={`Select ${displayName}`}
              />
            </div>
          );
        })}

        <div className="shrink-0 flex items-center justify-between px-4 py-3 rounded-xl bg-secondary">
          <span className="text-sm text-secondary">User not here?</span>
          <Button
            size="sm"
            color="primary"
            type="button"
            iconLeading={Plus}
            onClick={() => {
              onNavigateAway?.();
              navigate({ to: "/admin/users" });
            }}
          >
            Add New User
          </Button>
        </div>
      </div>
    </div>
  );
};
